import { Box } from '@mui/material';
import {
  AssessmentsGrade,
  AssessmentWithEntries,
  FilterKeys,
  isAssessmentEntryComment,
} from '@schooly/api';
import { AssessmentsArrangeBy } from '@schooly/components/filters';
import { MD_BREAKPOINT_TABLE_MIN_WIDTH } from '@schooly/constants';
import { useAssessmentLabels } from '@schooly/hooks/use-assessment-labels';
import { FC, Fragment, useEffect, useMemo, useState } from 'react';

import { TagGroup } from '../../uikit/TagGroup/TagGroup';
import { GridContainer } from '../Grid/Grid';
import { ListViewAssessmentEntriesRow } from '../ListViewAssessmentEntriesRow/ListViewAssessmentEntriesRow';
import { ListViewAssessmentEntriesHeader } from '../ListViewAssessmentEntriesRow/ListViewAssessmentsEntriesHeader';

const getRowId = (assessment: AssessmentWithEntries) =>
  `${assessment.id}-${assessment.groups[0]?.id ?? ''}`;

export interface ListViewAssessmentEntriesProps {
  assessments: AssessmentWithEntries[];
  lists?: Record<string, AssessmentsGrade>;
  relationId: string;
  showAllComments?: boolean;
  wideComments?: boolean;
  onAllCommentsClosed?: () => void;
  onAllCommentsOpen?: () => void;
  fullView: boolean;
  arrangeBy: AssessmentsArrangeBy;
}

export const ListViewAssessmentEntries: FC<ListViewAssessmentEntriesProps> = ({
  assessments,
  lists,
  relationId,
  showAllComments,
  wideComments,
  onAllCommentsClosed,
  onAllCommentsOpen,
  fullView,
  arrangeBy,
}) => {
  const { getAssessmentsArrangeByTitle } = useAssessmentLabels();

  const rowsWithComments = useMemo(() => {
    return assessments
      .filter((assessment) => {
        const group = assessment.groups[0];
        if (!group) return false;
        return group.students.some(
          (s) =>
            s.relation_id === relationId &&
            s.entries.some((e) => isAssessmentEntryComment(e) && e.comment),
        );
      })
      .map(getRowId);
  }, [assessments, relationId]);

  const [rowsWithOpenComments, setRowsWithOpenComments] = useState<string[]>([]);

  const addRowWithOpenComment = (rowId: string) => {
    setRowsWithOpenComments((old) => {
      if (old.includes(rowId)) {
        return old;
      } else {
        return [...old, rowId];
      }
    });
  };

  const allCommentsAreOpen = useMemo(() => {
    if (!rowsWithOpenComments.length) return false;

    return !rowsWithComments.some((rowId) => !rowsWithOpenComments.includes(rowId));
  }, [rowsWithComments, rowsWithOpenComments]);

  const renderHeader = () => {
    if (arrangeBy === FilterKeys.Date) return null;

    const assessment = assessments[0];
    const group = assessment.groups[0];

    return (
      <ListViewAssessmentEntriesHeader
        title={getAssessmentsArrangeByTitle(arrangeBy, assessment)}
        content={
          arrangeBy === FilterKeys.Subject && group ? <TagGroup>{group.name}</TagGroup> : undefined
        }
      />
    );
  };

  const renderSubHeader = (assessments: AssessmentWithEntries[], index: number) => {
    if (arrangeBy !== FilterKeys.Subject || index === 0) return null;

    const assessment = assessments[index];
    const prevAssessment = assessments[index - 1];
    const group = assessment.groups[0];
    const prevGroup = prevAssessment.groups[0];

    if (prevGroup?.id && group?.id && prevGroup.id !== group.id) {
      return <ListViewAssessmentEntriesHeader content={<TagGroup>{`${group.name}`}</TagGroup>} />;
    }

    return null;
  };

  useEffect(() => {
    if (!rowsWithOpenComments.length && onAllCommentsClosed) {
      onAllCommentsClosed();
    }
  }, [rowsWithOpenComments.length, onAllCommentsClosed]);

  useEffect(() => {
    if (allCommentsAreOpen && onAllCommentsOpen) {
      onAllCommentsOpen();
    }
  }, [allCommentsAreOpen, onAllCommentsOpen]);

  return (
    <Box
      sx={(theme) => ({
        [theme.breakpoints.down('md')]: {
          overflowX: fullView ? undefined : 'scroll',
        },
        [theme.breakpoints.down('sm')]: {
          overflowX: 'scroll',
        },
      })}
    >
      <GridContainer
        sx={(theme) => ({
          overflow: 'visible',
          [theme.breakpoints.down('md')]: {
            minWidth: fullView ? 0 : MD_BREAKPOINT_TABLE_MIN_WIDTH,
            overflowY: 'hidden',
            py: 0.5,
          },
          [theme.breakpoints.down('sm')]: {
            minWidth: MD_BREAKPOINT_TABLE_MIN_WIDTH,
          },
        })}
      >
        {renderHeader()}

        {assessments.map((assessment, index, assessments) => {
          const rowId = getRowId(assessment);

          return (
            <Fragment key={rowId}>
              {renderSubHeader(assessments, index)}
              <ListViewAssessmentEntriesRow
                assessment={assessment as AssessmentWithEntries}
                lists={lists}
                relationId={relationId}
                showAllComments={showAllComments}
                wideComments={wideComments}
                isCommentsOpen={rowsWithOpenComments.includes(rowId)}
                onOpenComments={() => addRowWithOpenComment(rowId)}
                onCloseComments={() =>
                  setRowsWithOpenComments((old) => old.filter((id) => id !== rowId))
                }
                fullView={!!fullView}
                arrangeBy={arrangeBy}
              />
            </Fragment>
          );
        })}
      </GridContainer>
    </Box>
  );
};
